import React from 'react';
import { Alert, Box, Typography, ToggleButtonGroup, ToggleButton, IconButton } from '@mui/material';
import { Bookmark as BookmarkIcon, BookmarkBorder as BookmarkBorderIcon } from '@mui/icons-material';
import { Button } from 'components/index';
import { SectionFieldDTO } from '../magic-report.type';
import { ProjectLineItemDTO } from '../../../project/project.type';
import { STORED_MATERIALS_G703_NO_REQUEST_TEXT } from './sections.config';
import { usePrompt } from '../../../../components/AppPrompt';
import { SINGLE_SELECT_TOGGLE_SX } from './sections.style';

export interface StoredMaterialRowDrawScope {
  /**
   * Which covered payapp (G702/draw) this row belongs to. All payapps' rows share the single
   * `StoredMaterialsRows` field so the backend's reset transform keeps matching that field name,
   * so the payapp is recorded per row. Absent on rows saved before multi-draw; those bind to the
   * first covered payapp on load.
   */
  drawId?: string;
}

export interface StoredMaterialRow extends StoredMaterialRowDrawScope {
  lineItemId: string;
  description: string;
  storedThisPeriod: number;
  storedOffsite: 'Yes' | 'No';
  visitedOffsite: 'Yes' | 'No';
  insuranceCert: 'Yes' | 'No';
  invoicePresent: 'Yes' | 'No';
  photosReceived: 'Yes' | 'No';
}

const parseRow = (value?: string): StoredMaterialRow | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed as StoredMaterialRow;
  } catch {
    return null;
  }
};

const serializeRow = (row: StoredMaterialRow): string => JSON.stringify(row);

const ensureRowDefaults = (row: Partial<StoredMaterialRow>): StoredMaterialRow => ({
  lineItemId: row.lineItemId || '',
  description: row.description || '',
  storedThisPeriod: row.storedThisPeriod ?? 0,
  storedOffsite: row.storedOffsite || 'No',
  visitedOffsite: row.visitedOffsite || 'No',
  insuranceCert: row.insuranceCert || 'No',
  invoicePresent: row.invoicePresent || 'No',
  photosReceived: row.photosReceived || 'No',
  // Preserved so a row never loses which payapp it belongs to when normalized.
  drawId: row.drawId,
});

interface StoredMaterialsTableProps {
  rowsField: SectionFieldDTO | undefined;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  lineItems: ProjectLineItemDTO[] | undefined;
  onRowsSynced?: (rows: StoredMaterialRow[]) => void;
  onSyncFunctionReady?: (syncFn: () => Promise<void>) => void;
  /** Whether an empty table may fill itself from the budget (first open of a period only). */
  autoSync?: boolean;
}

export const StoredMaterialsTable: React.FC<StoredMaterialsTableProps> = ({
  rowsField,
  handleFieldChange,
  lineItems,
  onRowsSynced,
  onSyncFunctionReady,
  autoSync = false,
}) => {
  const { getConfirmation } = usePrompt();
  const currentRows: StoredMaterialRow[] =
    rowsField?.entries?.map(e => parseRow(e.value)).filter((r): r is StoredMaterialRow => r !== null) || [];

  const buildRowsFromLineItems = (): StoredMaterialRow[] => {
    const storedItems = (lineItems || []).filter(li => {
      return (li.storedThisPeriod || 0) > 0;
    });

    if (!storedItems.length) {
      return [];
    }

    return storedItems.map(li => {
      const existing = currentRows.find(r => r.lineItemId === li.id);
      if (existing) {
        return ensureRowDefaults({
          ...existing,
          storedThisPeriod: li.storedThisPeriod || 0,
          description: li.lineItem?.description || existing.description,
        });
      }
      return ensureRowDefaults({
        lineItemId: li.id,
        description: li.lineItem?.description || '',
        storedThisPeriod: li.storedThisPeriod || 0,
      });
    });
  };

  const syncRowsFromBudget = async (skipConfirmation = false) => {
    if (!skipConfirmation) {
      const isConfirmed = await getConfirmation({
        heading: 'Refresh Stored Materials',
        description:
          'This will overwrite the stored materials table with the latest data from the budget. Any manual edits you have made will be lost. Are you sure you want to continue?',
        confirmButton: {
          label: 'Continue',
          color: 'primary',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });

      if (!isConfirmed) return;
    }

    const nextRows = buildRowsFromLineItems();

    const entries =
      nextRows.length === 0
        ? []
        : nextRows.map((row, index) => ({
            order: index,
            value: serializeRow(row),
          }));

    handleFieldChange('StoredMaterialsRows', {
      entries,
    });

    if (!nextRows.length) {
      handleFieldChange('G703MaterialsIntroComment', {
        value: STORED_MATERIALS_G703_NO_REQUEST_TEXT,
      });
      handleFieldChange('G703MaterialsComment', {
        value: '',
      });
    }

    onRowsSynced?.(nextRows);
  };

  // Note: Auto-sync removed - user must manually click "Get Stored Materials" button
  // This prevents overwriting saved data without confirmation

  const rowsToRender = currentRows.length || !autoSync ? currentRows : buildRowsFromLineItems();

  // Auto-save budget data to entries if it's being displayed but not yet saved
  // This ensures anything rendered in the table is also persisted to the backend
  const hasAutoSavedRef = React.useRef(false);
  React.useEffect(() => {
    // Only auto-save once on initial load if:
    // 1. We have no saved entries (currentRows.length === 0)
    // 2. But we have budget data to display (buildRowsFromLineItems returns rows)
    // 3. And lineItems are available (component has loaded budget data)
    // 4. And we haven't already auto-saved
    if (autoSync && !hasAutoSavedRef.current && currentRows.length === 0 && lineItems && lineItems.length > 0) {
      const budgetRows = buildRowsFromLineItems();
      if (budgetRows.length > 0) {
        // Save budget-derived rows to entries so they persist to backend
        const entries = budgetRows.map((row, index) => ({
          order: index,
          value: serializeRow(row),
        }));
        handleFieldChange('StoredMaterialsRows', {
          entries,
        });
        onRowsSynced?.(budgetRows);
        hasAutoSavedRef.current = true;
      }
    }
    // Reset ref if entries are cleared (user deleted all rows)
    if (currentRows.length === 0 && (!lineItems || lineItems.length === 0)) {
      hasAutoSavedRef.current = false;
    }
  }, [lineItems, currentRows.length, autoSync]); // Run when lineItems or saved entries change

  // Expose sync function to parent
  React.useEffect(() => {
    if (onSyncFunctionReady) {
      onSyncFunctionReady(() => syncRowsFromBudget(true));
    }
  }, [onSyncFunctionReady]);

  const handleToggleChange = (
    row: StoredMaterialRow,
    field: keyof Omit<StoredMaterialRow, 'lineItemId' | 'description' | 'storedThisPeriod'>,
    value: 'Yes' | 'No',
  ) => {
    // Recalculate current rows from field to ensure we have latest data
    const savedRows: StoredMaterialRow[] =
      rowsField?.entries?.map(e => parseRow(e.value)).filter((r): r is StoredMaterialRow => r !== null) || [];
    // If nothing saved yet, fall back to the currently rendered rows so first edit persists
    const latestRows: StoredMaterialRow[] = savedRows.length ? savedRows : rowsToRender;

    const updatedRow = { ...row, [field]: value } as StoredMaterialRow;
    const updatedRows = latestRows.map(r => (r.lineItemId === row.lineItemId ? updatedRow : r));
    const entries = updatedRows.map((r, index) => ({
      order: index,
      value: serializeRow(r),
    }));
    handleFieldChange('StoredMaterialsRows', { entries });
    onRowsSynced?.(updatedRows);
  };
  const hasAnyOffsite = rowsToRender.some(r => r.storedOffsite === 'Yes');

  // Define grid template based on whether we have offsite items
  // Description flexes; toggle/amount columns are fixed so they don't stretch at wide viewports
  const GRID_TEMPLATE = hasAnyOffsite ? 'minmax(120px, 1fr) 130px 110px repeat(4, 110px)' : 'minmax(120px, 1fr) 130px 110px';

  const addToActionItems = rowsField?.addToActionItems || false;

  const handleBookmarkToggle = () => {
    handleFieldChange('StoredMaterialsRows', { addToActionItems: !addToActionItems });
  };

  const storedItems = (lineItems || []).filter(li => (li.storedThisPeriod || 0) > 0);
  const isOutOfDate =
    !autoSync &&
    (storedItems.length !== currentRows.length ||
      storedItems.some(li => {
        const row = currentRows.find(r => r.lineItemId === li.id);
        return !row || Math.abs((row.storedThisPeriod || 0) - (li.storedThisPeriod || 0)) > 0.005;
      }));
  const outOfDateAlert = isOutOfDate ? (
    <Alert severity='warning' sx={{ mb: 1, fontSize: '0.875rem' }}>
      Stored Materials are not synced with the current budget. Click &quot;Get Stored Materials&quot; to refresh the data.
    </Alert>
  ) : null;

  if (!rowsToRender.length) {
    return (
      <Box sx={{ mt: 1, mb: 2 }}>
        {outOfDateAlert}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            No line items with a value in &quot;Stored This Period&quot; were found in the Review Budget step.
          </Typography>
          <Button variant='outlined' size='small' onClick={syncRowsFromBudget}>
            Get Stored Materials
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      {outOfDateAlert}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant='body2' color='text.secondary'>
          Line items with Stored This Period &gt; 0 from Review Budget.
        </Typography>
        <Button variant='outlined' size='small' onClick={syncRowsFromBudget}>
          Get Stored Materials
        </Button>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }}>
        {/* Scrollable wrapper — Accordion overflow-x is set to visible in MagicReportSection */}
        <Box sx={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
          {/* minWidth: 100% ensures it fills the container; width: max-content lets it grow wider when grid overflows so the scroll wrapper picks it up */}
          <Box
            sx={{
              minWidth: '100%',
              width: 'max-content',
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {/* Header Row */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID_TEMPLATE,
                alignItems: 'center',
                bgcolor: 'grey.100',
                px: 2,
                py: 1.5,
                columnGap: 2,
              }}
            >
              <Typography variant='body2' fontWeight='medium'>
                Description
              </Typography>
              <Typography variant='body2' fontWeight='medium' sx={{ textAlign: 'center' }}>
                Stored Offsite?
              </Typography>
              <Typography variant='body2' fontWeight='medium' sx={{ textAlign: 'right' }}>
                Amount
              </Typography>
              {hasAnyOffsite && (
                <>
                  <Typography variant='body2' fontWeight='medium' sx={{ textAlign: 'center' }}>
                    Visited?
                  </Typography>
                  <Typography variant='body2' fontWeight='medium' sx={{ textAlign: 'center' }}>
                    Insurance?
                  </Typography>
                  <Typography variant='body2' fontWeight='medium' sx={{ textAlign: 'center' }}>
                    Invoice?
                  </Typography>
                  <Typography variant='body2' fontWeight='medium' sx={{ textAlign: 'center' }}>
                    Rec&apos;d Pics?
                  </Typography>
                </>
              )}
            </Box>

            {/* Data Rows */}
            {rowsToRender.map((row, index) => {
              const showDetailQuestions = row.storedOffsite === 'Yes';

              const renderToggle = (field: keyof StoredMaterialRow) => {
                if (field === 'lineItemId' || field === 'description' || field === 'storedThisPeriod') return null;
                const value = row[field] as 'Yes' | 'No';
                return (
                  <ToggleButtonGroup
                    exclusive
                    size='small'
                    value={value}
                    onChange={(_, val) => {
                      if (!val) return;
                      handleToggleChange(row, field as any, val);
                    }}
                    sx={SINGLE_SELECT_TOGGLE_SX}
                  >
                    <ToggleButton value='Yes'>Yes</ToggleButton>
                    <ToggleButton value='No'>No</ToggleButton>
                  </ToggleButtonGroup>
                );
              };

              return (
                <Box
                  key={row.lineItemId}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: GRID_TEMPLATE,
                    alignItems: 'center',
                    columnGap: 2,
                    px: 2,
                    py: 1.5,
                    bgcolor: index % 2 === 0 ? 'grey.50' : 'background.paper',
                    borderTop: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <Typography variant='body2'>{row.description}</Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>{renderToggle('storedOffsite')}</Box>

                  <Typography variant='body2' sx={{ textAlign: 'right' }}>
                    {row.storedThisPeriod.toLocaleString(undefined, { style: 'currency', currency: 'USD' })}
                  </Typography>

                  {hasAnyOffsite && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {showDetailQuestions ? renderToggle('visitedOffsite') : null}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {showDetailQuestions ? renderToggle('insuranceCert') : null}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {showDetailQuestions ? renderToggle('invoicePresent') : null}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {showDetailQuestions ? renderToggle('photosReceived') : null}
                      </Box>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
        <IconButton size='small' onClick={handleBookmarkToggle} sx={{ mt: 1.5, flexShrink: 0 }}>
          {addToActionItems ? (
            <BookmarkIcon color='primary' sx={{ fontSize: 20 }} />
          ) : (
            <BookmarkBorderIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};
